import * as THREE from 'three';
import { BlockId, isDoor, getBlockHardness, getToolType, getHarvestLevel, getRequiredToolType, getTileUV, CRACK_TILES } from '../world/BlockType.js';
import { REACH_DISTANCE, ATLAS_SIZE, TILE_SIZE, TILES_PER_ROW } from '../constants.js';
import { AABB } from '../physics/AABB.js';
import { isTool, getToolSpeed, isToolEffective, getToolHarvestLevel, damageTool } from '../items/ToolSystem.js';
import { ItemId, getBlockDrop, getWheatBonusSeeds, isEdible, getFoodValue } from '../items/ItemRegistry.js';
import { FarmingSystem } from '../world/FarmingSystem.js';

/**
 * Sistema de interacción con bloques - minería progresiva
 *
 * Los bloques ya no se rompen instantáneamente (excepto en modo creativo/volando).
 * El jugador debe mantener el click izquierdo para minar progresivamente.
 * Se muestra un overlay de grietas en 4 etapas.
 * Si el jugador mira a otro bloque o suelta el click, el progreso se reinicia.
 */
export class BlockInteraction {
  constructor(world, fpsCamera, inputManager, blockHighlight, player, inventory, soundManager) {
    this.world = world;
    this.camera = fpsCamera;
    this.input = inputManager;
    this.highlight = blockHighlight;
    this.player = player;
    this.inventory = inventory;
    this.sound = soundManager;
    this.targetBlock = null;
    this.placementPos = null;
    this.cooldown = 0;

    // External callbacks (set by main.js)
    this.onOpenChest = null;    // (x, y, z) => void
    this.onOpenFurnace = null;  // (x, y, z) => void
    this.onOpenEnchantTable = null; // () => void
    this.onOpenBlacksmith = null;   // () => void — agachado sobre la mesa
    this.onOpenBrewingStand = null; // () => void
    this.onUseBed = null;       // (x, y, z) => void
    this.onOpenCrafting3x3 = null; // () => void
    this.onBlockBroken = null;  // (x, y, z, blockId, drops) => void
    this.onEatFood = null;      // (itemId, foodValue) => void
    this.onCropPlanted = null;  // (x, y, z) => void
    this.onBoneMeal = null;     // (x, y, z) => boolean
    this.onBlockPlaced = null;  // (blockId, x, y, z) => void
    this.onActivatePortal = null; // (x, y, z) => void
    this.onToggleLever = null;  // (x, y, z) => void
    this.onPressButton = null;  // (x, y, z) => void
    this.onToggleTrapdoor = null; // (x, y, z) => void
    this.onEditSign = null;       // (x, y, z) => void
    this.onToggleJukebox = null;  // (x, y, z) => void
    this.onChangeNote = null;     // (x, y, z) => void
    this.onToggleCoffin = null;   // (x, y, z) => void
    this.onPlaceEnderEye = null;  // (x, y, z) => void

    // Estado de minería progresiva
    this._miningBlock = null;   // { x, y, z } del bloque que estamos minando
    this._miningProgress = 0;   // 0.0 a 1.0
    this._miningTime = 0;       // tiempo total necesario para romper (en segundos)
    this._miningDrops = true;   // si el bloque da drops al romperse
    this._crackOverlay = null;  // mesh del overlay de grietas
    this._crackStage = -1;      // etapa actual de grieta (0-3)

    // Eating system (hold right-click for 1 second)
    this._eatingTimer = 0;
    this._eatingDuration = 1.0; // seconds to eat
    this._eatingItemId = null;
    this._eatingFoodValue = null;
    this._eatingProgressBar = null;
    this._eatSoundTimer = 0;
    this._initEatingUI();

    // Crear material para overlay de grietas
    this._crackMaterial = null;
    this._initCrackMaterial();

    // Scroll wheel para cambiar selección de hotbar
    document.addEventListener('wheel', (e) => {
      if (e.deltaY > 0) {
        this.inventory.scrollSelection(1);
      } else {
        this.inventory.scrollSelection(-1);
      }
    });

    // Teclas numéricas 1-9 para seleccionar slot del hotbar
    document.addEventListener('keydown', (e) => {
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9) {
        this.inventory.selectSlot(num - 1);
      }
    });
  }

  // Initialize eating progress bar UI
  _initEatingUI() {
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      width: 120px;
      height: 8px;
      background: rgba(0,0,0,0.6);
      border: 1px solid #555;
      border-radius: 2px;
      z-index: 200;
      display: none;
    `;
    const fill = document.createElement('div');
    fill.style.cssText = `
      width: 0%;
      height: 100%;
      background: linear-gradient(to right, #8B6B3E, #D4A44A);
      border-radius: 1px;
      transition: width 0.05s linear;
    `;
    container.appendChild(fill);
    document.body.appendChild(container);
    this._eatingProgressBar = { container, fill };
  }

  // Cancel any in-progress eating
  _cancelEating() {
    this._eatingTimer = 0;
    this._eatingItemId = null;
    this._eatingFoodValue = null;
    this._eatSoundTimer = 0;
    if (this._eatingProgressBar) {
      this._eatingProgressBar.container.style.display = 'none';
      this._eatingProgressBar.fill.style.width = '0%';
    }
  }

  // Update eating progress (called from main update)
  _updateEating(dt) {
    // Check if right mouse is held and we're eating
    if (!this._eatingItemId) return;

    const isHoldingRight = this.input.isMouseDown(2);
    if (!isHoldingRight) {
      this._cancelEating();
      return;
    }

    // Verify we're still holding the same food item
    const selectedSlot = this.inventory.getSelectedSlot();
    if (!selectedSlot || selectedSlot.itemId !== this._eatingItemId) {
      this._cancelEating();
      return;
    }

    this._eatingTimer += dt;

    // Play eating sound periodically
    this._eatSoundTimer += dt;
    if (this._eatSoundTimer >= 0.25) {
      this._eatSoundTimer = 0;
      if (this.sound) this.sound.playEat();
    }

    // Update progress bar
    const progress = Math.min(1, this._eatingTimer / this._eatingDuration);
    if (this._eatingProgressBar) {
      this._eatingProgressBar.fill.style.width = (progress * 100) + '%';
    }

    // Eating complete
    if (this._eatingTimer >= this._eatingDuration) {
      const itemId = this._eatingItemId;
      const foodValue = this._eatingFoodValue;
      this._cancelEating();

      // Consume the food
      if (this.onEatFood) {
        this.onEatFood(itemId, foodValue);
      }
      selectedSlot.count--;
      if (selectedSlot.count <= 0) {
        this.inventory.slots[this.inventory.selectedIndex] = null;
      }
      this.cooldown = 0.3;
    }
  }

  // Inicializar el material del overlay de grietas usando el atlas de texturas
  _initCrackMaterial() {
    // Usamos un material transparente que se superpone sobre el bloque
    // La textura se asigna luego cuando se necesita
    this._crackMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.FrontSide,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });
  }

  // Crear/actualizar el mesh del overlay de grietas sobre un bloque
  _updateCrackOverlay(x, y, z, stage) {
    if (stage === this._crackStage &&
        this._crackOverlay &&
        this._crackOverlay.position.x === x + 0.5 &&
        this._crackOverlay.position.y === y + 0.5 &&
        this._crackOverlay.position.z === z + 0.5) {
      return; // No hay cambios
    }

    this._removeCrackOverlay();
    if (stage < 0 || stage > 3) return;

    this._crackStage = stage;

    // Crear un cubo ligeramente más grande que el bloque con la textura de grieta
    const tileIndex = CRACK_TILES[stage];

    // Crear canvas individual para la textura de grieta
    const canvas = document.createElement('canvas');
    canvas.width = TILE_SIZE;
    canvas.height = TILE_SIZE;
    const ctx = canvas.getContext('2d');

    // Obtener el atlas texture del world (del mesher)
    // En vez de eso, generamos la textura de grieta proceduralmente
    this._drawCrackTexture(ctx, TILE_SIZE, stage);

    const crackTexture = new THREE.CanvasTexture(canvas);
    crackTexture.magFilter = THREE.NearestFilter;
    crackTexture.minFilter = THREE.NearestFilter;

    const material = new THREE.MeshBasicMaterial({
      map: crackTexture,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.FrontSide,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    const geometry = new THREE.BoxGeometry(1.002, 1.002, 1.002);
    this._crackOverlay = new THREE.Mesh(geometry, material);
    this._crackOverlay.position.set(x + 0.5, y + 0.5, z + 0.5);
    this.world.scene.add(this._crackOverlay);
  }

  // Dibujar textura de grieta proceduralmente (misma lógica que el atlas)
  _drawCrackTexture(ctx, s, stage) {
    ctx.clearRect(0, 0, s, s);

    if (stage === 0) {
      // Grieta etapa 1 - grietas leves
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(4, 2);
      ctx.lineTo(7, 6);
      ctx.lineTo(5, 10);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(11, 5);
      ctx.lineTo(9, 9);
      ctx.stroke();
    } else if (stage === 1) {
      // Grieta etapa 2 - grietas moderadas
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(3, 1);
      ctx.lineTo(6, 5);
      ctx.lineTo(4, 9);
      ctx.lineTo(6, 13);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(10, 2);
      ctx.lineTo(8, 7);
      ctx.lineTo(11, 11);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(6, 5);
      ctx.lineTo(10, 6);
      ctx.stroke();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
      ctx.fillRect(5, 4, 2, 2);
      ctx.fillRect(9, 8, 2, 2);
    } else if (stage === 2) {
      // Grieta etapa 3 - grietas severas
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(2, 0);
      ctx.lineTo(5, 4);
      ctx.lineTo(3, 8);
      ctx.lineTo(5, 12);
      ctx.lineTo(3, 15);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(10, 0);
      ctx.lineTo(8, 5);
      ctx.lineTo(11, 9);
      ctx.lineTo(9, 14);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(5, 4);
      ctx.lineTo(8, 5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(3, 8);
      ctx.lineTo(7, 7);
      ctx.lineTo(11, 9);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(13, 3);
      ctx.lineTo(11, 7);
      ctx.lineTo(14, 11);
      ctx.stroke();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.fillRect(4, 3, 3, 3);
      ctx.fillRect(8, 7, 3, 3);
      ctx.fillRect(2, 10, 2, 2);
    } else if (stage === 3) {
      // Grieta etapa 4 - a punto de romperse
      ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
      ctx.fillRect(0, 0, s, s);
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(1, 0); ctx.lineTo(4, 4); ctx.lineTo(2, 8); ctx.lineTo(4, 12); ctx.lineTo(2, 15);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(7, 0); ctx.lineTo(5, 4); ctx.lineTo(8, 8); ctx.lineTo(6, 12); ctx.lineTo(8, 15);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(12, 0); ctx.lineTo(10, 4); ctx.lineTo(13, 8); ctx.lineTo(11, 12); ctx.lineTo(13, 15);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, 4); ctx.lineTo(15, 5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, 9); ctx.lineTo(15, 8);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, 13); ctx.lineTo(15, 12);
      ctx.stroke();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.fillRect(3, 2, 4, 3);
      ctx.fillRect(9, 6, 4, 3);
      ctx.fillRect(1, 10, 4, 3);
      ctx.fillRect(10, 11, 3, 3);
    }
  }

  // Eliminar el overlay de grietas
  _removeCrackOverlay() {
    if (this._crackOverlay) {
      this.world.scene.remove(this._crackOverlay);
      if (this._crackOverlay.geometry) this._crackOverlay.geometry.dispose();
      if (this._crackOverlay.material) {
        if (this._crackOverlay.material.map) this._crackOverlay.material.map.dispose();
        this._crackOverlay.material.dispose();
      }
      this._crackOverlay = null;
    }
    this._crackStage = -1;
  }

  // Reiniciar el progreso de minería
  _resetMining() {
    this._miningBlock = null;
    this._miningProgress = 0;
    this._miningTime = 0;
    this._miningDrops = true;
    this._removeCrackOverlay();
  }

  // Calcular el tiempo de minería para un bloque con la herramienta actual
  _calculateMiningTime(blockId) {
    const hardness = getBlockHardness(blockId);

    // Bloques instantáneos (hardness = 0)
    if (hardness === 0) return { time: 0, canHarvest: true };
    // Bloques irrompibles
    if (hardness === Infinity) return { time: Infinity, canHarvest: false };

    const selectedItem = this.inventory.getSelectedSlot();
    const blockToolType = getToolType(blockId);
    const requiredToolType = getRequiredToolType(blockId);
    const harvestLevel = getHarvestLevel(blockId);

    let speedMultiplier = 1; // velocidad base (mano)
    let canHarvest = true;

    if (isTool(selectedItem)) {
      const toolLevel = getToolHarvestLevel(selectedItem);
      const isEffective = isToolEffective(selectedItem, blockToolType);

      // Verificar si la herramienta tiene nivel suficiente
      if (requiredToolType && selectedItem.toolType === requiredToolType && toolLevel >= harvestLevel) {
        // Herramienta correcta con nivel suficiente
        speedMultiplier = getToolSpeed(selectedItem);
        canHarvest = true;
      } else if (requiredToolType && selectedItem.toolType === requiredToolType && toolLevel < harvestLevel) {
        // Herramienta correcta pero nivel insuficiente - 5x más lento, sin drops
        speedMultiplier = getToolSpeed(selectedItem);
        canHarvest = false;
      } else if (isEffective) {
        // Herramienta efectiva para este tipo de bloque
        speedMultiplier = getToolSpeed(selectedItem);
        canHarvest = toolLevel >= harvestLevel;
      } else {
        // Herramienta incorrecta
        canHarvest = (harvestLevel === 0); // solo da drops si no requiere herramienta especial
      }
    } else {
      // Mano desnuda
      canHarvest = (harvestLevel === 0);
    }

    // Tiempo base: hardness * 1.5 segundos (con la herramienta correcta)
    // Sin herramienta correcta: hardness * 5 segundos
    let baseTime;
    if (speedMultiplier > 1) {
      baseTime = (hardness * 1.5) / speedMultiplier;
    } else {
      baseTime = hardness * 5;
    }

    // Si no puede cosechar (nivel insuficiente), 5x más lento
    if (!canHarvest) {
      baseTime *= 5;
    }

    // Mínimo 0.05 segundos para dar sensación de que algo pasó
    return { time: Math.max(baseTime, 0.05), canHarvest };
  }

  update(dt) {
    this.cooldown -= dt;

    const origin = this.camera.camera.position.clone();
    const dir = this.camera.getLookDirection();
    const result = this._voxelRaycast(origin, dir, REACH_DISTANCE);

    if (result) {
      this.targetBlock = result.block;
      this.placementPos = result.adjacent;
      this.highlight.show(result.block.x, result.block.y, result.block.z);
    } else {
      this.targetBlock = null;
      this.placementPos = null;
      this.highlight.hide();
    }

    // --- MINERÍA PROGRESIVA (click izquierdo mantenido) ---
    this._updateMining(dt);

    // --- EATING PROGRESS (hold right-click) ---
    this._updateEating(dt);

    // --- COLOCAR BLOQUE (click derecho) ---
    if (this.input.consumeClick(2) && this.cooldown <= 0) {
      // Check if holding food first (start eating), then handle placement
      const selSlot = this.inventory.getSelectedSlot();
      if (selSlot && selSlot.itemId !== undefined && !this._eatingItemId) {
        const fv = getFoodValue(selSlot.itemId);
        if (fv && this.onEatFood) {
          // Start eating (don't consume yet)
          this._eatingTimer = 0;
          this._eatingItemId = selSlot.itemId;
          this._eatingFoodValue = fv;
          this._eatSoundTimer = 0.2; // play first sound quickly
          if (this._eatingProgressBar) {
            this._eatingProgressBar.container.style.display = 'block';
            this._eatingProgressBar.fill.style.width = '0%';
          }
          return; // don't place blocks while starting to eat
        }
      }
      if (this.targetBlock) {
        this._handlePlacement();
      }
    }
  }

  _updateMining(dt) {
    const isHoldingLeft = this.input.isMouseDown(0);
    const clickedLeft = this.input.consumeClick(0);

    // Si no hay bloque objetivo o no está presionando click izquierdo
    if (!this.targetBlock || !isHoldingLeft) {
      if (this._miningBlock) {
        this._resetMining();
      }
      return;
    }

    const t = this.targetBlock;
    const blockId = this.world.getBlock(t.x, t.y, t.z);

    // Intercept coffin left-click: toggle open/close instead of mining
    if (blockId === BlockId.COFFIN || blockId === BlockId.COFFIN_OPEN) {
      if (clickedLeft && this.onToggleCoffin) {
        this.onToggleCoffin(t.x, t.y, t.z);
        this.cooldown = 0.3;
      }
      this._resetMining();
      return; // never mine coffins
    }

    // No se puede romper bedrock ni agua
    if (blockId === BlockId.BEDROCK || blockId === BlockId.WATER) {
      this._resetMining();
      return;
    }

    // Modo creativo (volando) = romper instantáneo
    if (this.player.flying) {
      if (clickedLeft || isHoldingLeft) {
        // Solo romper con un click, no continuamente al mantener
        // Usamos cooldown para no romper demasiado rápido
        if (this.cooldown <= 0) {
          this._breakBlock(t.x, t.y, t.z, blockId, true);
          this.cooldown = 0.2;
        }
      }
      return;
    }

    // Verificar si cambió el bloque objetivo
    if (this._miningBlock &&
        (this._miningBlock.x !== t.x ||
         this._miningBlock.y !== t.y ||
         this._miningBlock.z !== t.z)) {
      // Cambió de bloque - reiniciar progreso
      this._resetMining();
    }

    // Iniciar minería si no hay una en curso
    if (!this._miningBlock) {
      const { time, canHarvest } = this._calculateMiningTime(blockId);

      // Bloques instantáneos (hardness 0)
      if (time <= 0.05) {
        this._breakBlock(t.x, t.y, t.z, blockId, canHarvest);
        this.cooldown = 0.15;
        return;
      }

      // Bloques irrompibles
      if (time === Infinity) return;

      this._miningBlock = { x: t.x, y: t.y, z: t.z };
      this._miningProgress = 0;
      this._miningTime = time;
      this._miningDrops = canHarvest;
    }

    // Avanzar progreso de minería
    this._miningProgress += dt / this._miningTime;

    // Calcular etapa de grieta (0-3)
    const crackStage = Math.min(3, Math.floor(this._miningProgress * 4));

    // Actualizar overlay visual
    if (crackStage >= 0) {
      this._updateCrackOverlay(this._miningBlock.x, this._miningBlock.y, this._miningBlock.z, crackStage);
    }

    // Bloque completamente minado
    if (this._miningProgress >= 1.0) {
      const mb = this._miningBlock;
      const minedBlockId = this.world.getBlock(mb.x, mb.y, mb.z);
      const drops = this._miningDrops;

      // Dañar la herramienta
      const selectedItem = this.inventory.getSelectedSlot();
      if (isTool(selectedItem)) {
        const result = damageTool(selectedItem);
        if (result === null) {
          // La herramienta se rompió
          this.inventory.slots[this.inventory.selectedIndex] = null;
          if (this.sound) this.sound.playBreak();
        }
      }

      this._resetMining();
      this._breakBlock(mb.x, mb.y, mb.z, minedBlockId, drops);
      this.cooldown = 0.15;
    }
  }

  // Romper un bloque (con o sin drops)
  _breakBlock(x, y, z, blockId, giveDrop) {
    if (isDoor(blockId)) {
      this.world.setBlock(x, y, z, BlockId.AIR);
      const above = this.world.getBlock(x, y + 1, z);
      const below = this.world.getBlock(x, y - 1, z);
      if (isDoor(above)) this.world.setBlock(x, y + 1, z, BlockId.AIR);
      if (isDoor(below)) this.world.setBlock(x, y - 1, z, BlockId.AIR);
      if (giveDrop) {
        this.inventory.addBlock(BlockId.DOOR_CLOSED);
      }
    } else {
      this.world.setBlock(x, y, z, BlockId.AIR);
      if (giveDrop) {
        // Use ItemRegistry for real drops
        const drop = getBlockDrop(blockId, null);
        if (drop) {
          if (drop.isBlock) {
            this.inventory.addBlock(drop.id);
          } else {
            this.inventory.addItem(drop.id, drop.count);
          }
        }
        // Bonus seeds for wheat
        if (blockId === BlockId.WHEAT) {
          const bonusSeeds = getWheatBonusSeeds();
          if (bonusSeeds) {
            this.inventory.addItem(bonusSeeds.id, bonusSeeds.count);
          }
        }
      }
    }
    // Notify main.js for particles/tile entity cleanup
    if (this.onBlockBroken) this.onBlockBroken(x, y, z, blockId, giveDrop);
    if (this.sound) this.sound.playBreak(blockId);
  }

  // Lógica de colocación de bloques
  _handlePlacement() {
    const t = this.targetBlock;
    const targetBlockId = this.world.getBlock(t.x, t.y, t.z);

    // Click derecho en una puerta la abre/cierra
    if (isDoor(targetBlockId)) {
      const newState = targetBlockId === BlockId.DOOR_CLOSED ? BlockId.DOOR_OPEN : BlockId.DOOR_CLOSED;
      this.world.setBlock(t.x, t.y, t.z, newState);
      const above = this.world.getBlock(t.x, t.y + 1, t.z);
      const below = this.world.getBlock(t.x, t.y - 1, t.z);
      if (isDoor(above)) this.world.setBlock(t.x, t.y + 1, t.z, newState);
      if (isDoor(below)) this.world.setBlock(t.x, t.y - 1, t.z, newState);
      this.cooldown = 0.2;
      if (this.sound) this.sound.playPlace();
      return;
    }

    // Click derecho en palanca -> alternar
    if (targetBlockId === BlockId.LEVER) {
      if (this.onToggleLever) {
        this.onToggleLever(t.x, t.y, t.z);
      }
      this.cooldown = 0.2;
      if (this.sound) this.sound.playPlace();
      return;
    }

    // Click derecho en boton -> pulso temporal
    if (targetBlockId === BlockId.BUTTON) {
      if (this.onPressButton) {
        this.onPressButton(t.x, t.y, t.z);
      }
      this.cooldown = 0.3;
      if (this.sound) this.sound.playPlace();
      return;
    }

    // Click derecho en cofre -> abrir UI de cofre
    if (targetBlockId === BlockId.CHEST && this.onOpenChest) {
      this.onOpenChest(t.x, t.y, t.z);
      this.cooldown = 0.3;
      return;
    }

    // Click derecho en mesa de crafteo -> crafteo 3x3
    if (targetBlockId === BlockId.CRAFTING_TABLE && this.onOpenCrafting3x3) {
      this.onOpenCrafting3x3();
      this.cooldown = 0.3;
      return;
    }

    // Click derecho en cama -> set spawn / dormir
    if (targetBlockId === BlockId.BED && this.onUseBed) {
      this.onUseBed(t.x, t.y, t.z);
      this.cooldown = 0.3;
      return;
    }

    // Click derecho en horno -> abrir UI de fundición
    if ((targetBlockId === BlockId.FURNACE || targetBlockId === BlockId.FURNACE_LIT) && this.onOpenFurnace) {
      this.onOpenFurnace(t.x, t.y, t.z);
      this.cooldown = 0.3;
      return;
    }

    // Click derecho en mesa de encantamientos -> abrir UI
    if (targetBlockId === BlockId.ENCHANTING_TABLE && this.onOpenEnchantTable) {
      /* Agachado abre el herrero; de pie, el encantador. Un bloque, dos
         servicios: es lo que hace el hub de Dungeons con sus dos NPC, sin
         obligar a Fede a recordar otro bloque distinto. */
      if (this.input?.keys?.ShiftLeft && this.onOpenBlacksmith) {
        this.onOpenBlacksmith();
        this.cooldown = 0.3;
        return;
      }
      this.onOpenEnchantTable();
      this.cooldown = 0.3;
      return;
    }

    // Click derecho en soporte de pociones -> abrir UI
    if (targetBlockId === BlockId.BREWING_STAND && this.onOpenBrewingStand) {
      this.onOpenBrewingStand();
      this.cooldown = 0.3;
      return;
    }

    // Click derecho en obsidiana -> intentar activar portal del Nether
    if (targetBlockId === BlockId.OBSIDIAN && this.onActivatePortal) {
      this.onActivatePortal(t.x, t.y, t.z);
      this.cooldown = 0.5;
      return;
    }

    // Click derecho en End Portal Frame con Ojo de Ender -> colocar ojo
    if (targetBlockId === BlockId.END_PORTAL_FRAME && this.onPlaceEnderEye) {
      this.onPlaceEnderEye(t.x, t.y, t.z);
      this.cooldown = 0.5;
      return;
    }

    // Click derecho en trapdoor -> abrir/cerrar
    if (targetBlockId === BlockId.TRAPDOOR && this.onToggleTrapdoor) {
      this.onToggleTrapdoor(t.x, t.y, t.z);
      this.cooldown = 0.2;
      if (this.sound) this.sound.playPlace();
      return;
    }

    // Click derecho en cartel -> editar texto
    if (targetBlockId === BlockId.SIGN && this.onEditSign) {
      this.onEditSign(t.x, t.y, t.z);
      this.cooldown = 0.3;
      return;
    }

    // Click derecho en jukebox -> reproducir/detener musica
    if (targetBlockId === BlockId.JUKEBOX && this.onToggleJukebox) {
      this.onToggleJukebox(t.x, t.y, t.z);
      this.cooldown = 0.3;
      return;
    }

    // Click derecho en noteblock -> cambiar tono
    if (targetBlockId === BlockId.NOTEBLOCK && this.onChangeNote) {
      this.onChangeNote(t.x, t.y, t.z);
      this.cooldown = 0.2;
      return;
    }

    // Hoe on dirt/grass -> farmland
    const selectedSlot = this.inventory.getSelectedSlot();
    if (selectedSlot && selectedSlot.toolType === 'hoe') {
      if (targetBlockId === BlockId.DIRT || targetBlockId === BlockId.GRASS) {
        this.world.setBlock(t.x, t.y, t.z, BlockId.FARMLAND);
        // Check hydration
        if (FarmingSystem.isHydrated(this.world, t.x, t.y, t.z)) {
          this.world.setBlock(t.x, t.y, t.z, BlockId.FARMLAND_WET);
        }
        // Damage the hoe
        if (isTool(selectedSlot)) {
          const result = damageTool(selectedSlot);
          if (result === null) {
            this.inventory.slots[this.inventory.selectedIndex] = null;
            if (this.sound) this.sound.playBreak();
          }
        }
        this.cooldown = 0.3;
        if (this.sound) this.sound.playPlace();
        return;
      }
    }

    // Seeds on farmland -> plant wheat
    if (selectedSlot && selectedSlot.itemId === ItemId.SEEDS) {
      if ((targetBlockId === BlockId.FARMLAND || targetBlockId === BlockId.FARMLAND_WET) &&
          this.world.getBlock(t.x, t.y + 1, t.z) === BlockId.AIR) {
        this.world.setBlock(t.x, t.y + 1, t.z, BlockId.WHEAT);
        selectedSlot.count--;
        if (selectedSlot.count <= 0) {
          this.inventory.slots[this.inventory.selectedIndex] = null;
        }
        if (this.onCropPlanted) this.onCropPlanted(t.x, t.y + 1, t.z);
        this.cooldown = 0.3;
        if (this.sound) this.sound.playPlace();
        return;
      }
    }

    // Carrot/Potato/Beetroot on farmland -> plant crop
    const CROP_ITEMS = {
      [-92]: BlockId.CARROT,   // CARROT_ITEM
      [-93]: BlockId.POTATO,   // POTATO_ITEM
      [-94]: BlockId.BEETROOT, // BEETROOT_ITEM
    };
    if (selectedSlot && selectedSlot.itemId && CROP_ITEMS[selectedSlot.itemId]) {
      if ((targetBlockId === BlockId.FARMLAND || targetBlockId === BlockId.FARMLAND_WET) &&
          this.world.getBlock(t.x, t.y + 1, t.z) === BlockId.AIR) {
        this.world.setBlock(t.x, t.y + 1, t.z, CROP_ITEMS[selectedSlot.itemId]);
        selectedSlot.count--;
        if (selectedSlot.count <= 0) this.inventory.slots[this.inventory.selectedIndex] = null;
        if (this.onCropPlanted) this.onCropPlanted(t.x, t.y + 1, t.z);
        this.cooldown = 0.3;
        if (this.sound) this.sound.playPlace();
        return;
      }
    }

    // Bone meal on crop -> instant grow
    if (selectedSlot && selectedSlot.itemId === ItemId.BONE_MEAL) {
      if ((targetBlockId === BlockId.WHEAT || targetBlockId === BlockId.CARROT ||
           targetBlockId === BlockId.POTATO || targetBlockId === BlockId.BEETROOT) && this.onBoneMeal) {
        if (this.onBoneMeal(t.x, t.y, t.z)) {
          selectedSlot.count--;
          if (selectedSlot.count <= 0) {
            this.inventory.slots[this.inventory.selectedIndex] = null;
          }
          this.cooldown = 0.3;
          return;
        }
      }
    }

    // Food eating is now handled via hold-to-eat in update() -- skip here

    // Colocar polvo de redstone como cable de redstone
    if (selectedSlot && selectedSlot.itemId === ItemId.REDSTONE_DUST && this.placementPos) {
      const p = this.placementPos;
      const belowBlock = this.world.getBlock(p.x, p.y - 1, p.z);
      if (belowBlock !== BlockId.AIR && belowBlock !== BlockId.WATER && this.world.getBlock(p.x, p.y, p.z) === BlockId.AIR) {
        const blockAABB = new AABB(p.x, p.y, p.z, p.x + 1, p.y + 1, p.z + 1);
        const playerAABB = AABB.fromPlayer(
          this.player.position.x, this.player.position.y, this.player.position.z
        );
        if (!blockAABB.intersects(playerAABB)) {
          this.world.setBlock(p.x, p.y, p.z, BlockId.REDSTONE_WIRE);
          selectedSlot.count--;
          if (selectedSlot.count <= 0) {
            this.inventory.slots[this.inventory.selectedIndex] = null;
          }
          this.cooldown = 0.2;
          if (this.sound) this.sound.playPlace();
          if (this.onBlockPlaced) this.onBlockPlaced(BlockId.REDSTONE_WIRE, p.x, p.y, p.z);
          return;
        }
      }
    }

    // Colocar bloque
    if (this.placementPos) {
      const selectedBlock = this.inventory.getSelectedBlock();
      if (selectedBlock !== null) {
        const p = this.placementPos;
        const blockAABB = new AABB(p.x, p.y, p.z, p.x + 1, p.y + 1, p.z + 1);
        const playerAABB = AABB.fromPlayer(
          this.player.position.x, this.player.position.y, this.player.position.z
        );
        if (!blockAABB.intersects(playerAABB)) {
          // Puertas: colocar 2 bloques de alto
          if (selectedBlock === BlockId.DOOR_CLOSED) {
            const aboveBlock = this.world.getBlock(p.x, p.y + 1, p.z);
            if (aboveBlock === BlockId.AIR) {
              const topAABB = new AABB(p.x, p.y + 1, p.z, p.x + 1, p.y + 2, p.z + 1);
              if (!topAABB.intersects(playerAABB)) {
                this.inventory.consumeSelected();
                this.world.setBlock(p.x, p.y, p.z, BlockId.DOOR_CLOSED);
                this.world.setBlock(p.x, p.y + 1, p.z, BlockId.DOOR_CLOSED);
                this.cooldown = 0.2;
                if (this.sound) this.sound.playPlace();
              }
            }
          } else {
            this.inventory.consumeSelected();
            this.world.setBlock(p.x, p.y, p.z, selectedBlock);
            this.cooldown = 0.2;
            if (this.sound) this.sound.playPlace(selectedBlock);
            if (this.onBlockPlaced) this.onBlockPlaced(selectedBlock, p.x, p.y, p.z);
          }
        }
      }
    }
  }

  _voxelRaycast(origin, direction, maxDist) {
    let x = Math.floor(origin.x);
    let y = Math.floor(origin.y);
    let z = Math.floor(origin.z);

    const stepX = direction.x >= 0 ? 1 : -1;
    const stepY = direction.y >= 0 ? 1 : -1;
    const stepZ = direction.z >= 0 ? 1 : -1;

    const tDeltaX = direction.x !== 0 ? Math.abs(1 / direction.x) : Infinity;
    const tDeltaY = direction.y !== 0 ? Math.abs(1 / direction.y) : Infinity;
    const tDeltaZ = direction.z !== 0 ? Math.abs(1 / direction.z) : Infinity;

    let tMaxX = direction.x !== 0
      ? (direction.x >= 0 ? (x + 1 - origin.x) : (x - origin.x)) / direction.x
      : Infinity;
    let tMaxY = direction.y !== 0
      ? (direction.y >= 0 ? (y + 1 - origin.y) : (y - origin.y)) / direction.y
      : Infinity;
    let tMaxZ = direction.z !== 0
      ? (direction.z >= 0 ? (z + 1 - origin.z) : (z - origin.z)) / direction.z
      : Infinity;

    let prevX = x, prevY = y, prevZ = z;
    let t = 0;

    while (t < maxDist) {
      const block = this.world.getBlock(x, y, z);
      // Ignorar aire y agua para targeting
      if (block !== BlockId.AIR && block !== BlockId.WATER) {
        return {
          block: { x, y, z },
          adjacent: { x: prevX, y: prevY, z: prevZ },
          distance: t,
        };
      }

      prevX = x; prevY = y; prevZ = z;

      if (tMaxX < tMaxY) {
        if (tMaxX < tMaxZ) {
          t = tMaxX; x += stepX; tMaxX += tDeltaX;
        } else {
          t = tMaxZ; z += stepZ; tMaxZ += tDeltaZ;
        }
      } else {
        if (tMaxY < tMaxZ) {
          t = tMaxY; y += stepY; tMaxY += tDeltaY;
        } else {
          t = tMaxZ; z += stepZ; tMaxZ += tDeltaZ;
        }
      }
    }

    return null;
  }
}
